import { Typography } from 'antd'
import { AiOutlineDatabase } from 'react-icons/ai'

import { CheckGlobalResourceAccessTask } from '@/Ship/Tasks/CheckGlobalResourceAccessTask'
import { renderDateTimeView, renderUsersView } from '@/Ship/Helpers/fieldViews'

import {
    OTHER_CARRIER_EDRPOU_MAX_LENGTH,
    OTHER_CARRIER_FIELD_LABELS as L,
    OTHER_CARRIER_RESOURCE,
    OTHER_CARRIER_REST_URL,
    OTHER_CARRIER_TEXT_MAX_LENGTH,
    OTHER_CARRIER_TITLE_MAX_LENGTH,
} from '../Contracts/IOtherCarrier'
import { OtherCarrierFormTemplate } from '../Templates/OtherCarrierFormTemplate'

import type { OtherCarrierDictionaryRef } from '../Contracts/IOtherCarrier'
import type { IResourceConfig } from '@/Core/Contracts/UI/Resource'
import type { IFieldConfig } from '@/Core/Contracts/UI/Form'
import type { ReactNode } from 'react'

interface OtherCarrierRow {
    carTypes?: OtherCarrierDictionaryRef[] | null
    countries?: OtherCarrierDictionaryRef[] | null
    contactInformation?: string | null
    description?: string | null
    comment?: string | null
    _original?: OtherCarrierRow
}

/** Довгі тексти в колонці — одним рядком із трикрапкою, повний текст у підказці. */
const TEXT_COLUMNS = ['contactInformation', 'description', 'comment'] as const

/** Ширина колонки довгого тексту. */
const TEXT_COLUMN_WIDTH = 220

const sourceOf = (row: unknown): OtherCarrierRow => {
    const item = (row ?? {}) as OtherCarrierRow

    return item._original ?? item
}

/** Назви рядків довідника через «, » — як у колонках легасі. */
const titlesOf = (items: OtherCarrierDictionaryRef[] | null | undefined): string =>
    (items ?? [])
        .map((item) => (typeof item?.title === 'string' ? item.title : ''))
        .filter((title) => title !== '')
        .join(', ')

/**
 * Колонки, які ядро само не намалює як треба (`SPEC-010` §9.7):
 *
 *  - **«Типи авто»**, **«Країни перевезення»** — назви через «, »;
 *  - **«Контактна інформація»**, **«Опис»**, **«Коментар»** — один рядок із
 *    трикрапкою, повний текст у підказці: багаторядковий опис інакше
 *    розтягнув би рядок таблиці на пів екрана.
 */
const AddOtherCarrierColumnRenderers = ({ columns }: { columns: Array<Record<string, unknown>> }) => {
    columns.forEach((column) => {
        const key = String(column['key'] ?? '')

        if (key === 'carTypes' || key === 'countries') {
            column['render'] = (_: unknown, row: unknown): ReactNode => titlesOf(sourceOf(row)[key]) || '—'
        }

        if ((TEXT_COLUMNS as readonly string[]).includes(key)) {
            column['width'] = TEXT_COLUMN_WIDTH
            column['render'] = (_: unknown, row: unknown): ReactNode => {
                const value = sourceOf(row)[key as (typeof TEXT_COLUMNS)[number]]
                const text = typeof value === 'string' ? value.trim() : ''

                return text !== '' ? (
                    <Typography.Text ellipsis={{ tooltip: text }} style={{ width: TEXT_COLUMN_WIDTH, display: 'block' }}>
                        {text.replace(/\s*\n\s*/g, ' ')}
                    </Typography.Text>
                ) : (
                    '—'
                )
            }
        }
    })

    return columns
}

/** Лише перегляд: ставить сервер (аудит). */
const readOnly = (field: IFieldConfig): IFieldConfig => ({
    ...field,
    isEditable: false,
    inputProps: { ...(field.inputProps ?? {}), disabled: true },
})

/** Довгий текст: багаторядкове поле до 4096 символів (межа беку). */
const textField = (label: string, isRequired = false): IFieldConfig => ({
    label,
    type: 'textarea',
    isRequired,
    inputProps: { maxLength: OTHER_CARRIER_TEXT_MAX_LENGTH },
})

/**
 * Тіло запиту картки перед відправкою: службових полів бек однаково не
 * приймає (`other_carrier:create` / `:update`) — не шлемо їх, щоб тіло не
 * обіцяло зміни, якої не буде. Зовнішній код у формі не редагують.
 */
const READ_ONLY_KEYS = ['id', 'externalCode', 'isRemoved', 'createdAt', 'createdBy', 'updatedAt', 'updatedBy'] as const

const prepareOtherCarrierPayload = ({ values }: { values: Record<string, unknown> }): void => {
    READ_ONLY_KEYS.forEach((key) => delete values[key])
}

/**
 * «Інші перевізники» (`SPEC-010` §9.7): реєстр логістів на 8 колонок і 8
 * фільтрів у порядку легасі (ядро легасі показувало «Ід» і всі поля ресурсу
 * в порядку оголошення), 20 рядків на сторінку, новіші зміни зверху.
 *
 * **Усе — лише логістам** (`OtherCarrierRule`; з ієрархією — SL, A, SA):
 *  - пункт меню — за `read_collection` (`GATED_MENU_ITEMS`);
 *  - сторінку прямою адресою решта бачить як «Відсутній доступ»
 *    (`pageConfig.accessFn`), а API відповідає 403;
 *  - «Додати» — колекційне `create`, «Редагувати» / «Видалити» і
 *    «Коментувати» — `_permissions` запису.
 *
 * Ролей ресурс не питає (`SPEC-003` §1.3). Обраного, експорту, рядка статусів
 * та історії змін немає — як у легасі (ADR 041).
 */
export const OtherCarrierResource: IResourceConfig = {
    title: 'Інші перевізники',
    fields: {
        id: {
            label: L.id,
            type: 'number',
            isEditable: false,
        },
        title: {
            label: L.title,
            isRequired: true,
            // Частина назви, без урахування регістру (бек — ICU `like`)
            filterOperator: 'like',
            inputProps: { maxLength: OTHER_CARRIER_TITLE_MAX_LENGTH },
        },
        carTypes: {
            label: L.carTypes,
            type: 'select',
            isMultiple: true,
            isRequired: true,
            // Мультивибір у фільтрі: «будь-який з обраних»
            isFilterMultiple: true,
            resource: 'Setting.ListCarType',
        },
        countries: {
            label: L.countries,
            type: 'select',
            isMultiple: true,
            isRequired: true,
            isFilterMultiple: true,
            resource: 'Setting.ListCountry',
        },
        edrpou: {
            label: L.edrpou,
            isRequired: true,
            // Точний збіг, як у легасі (`SearchFilter edrpou exact`); довжину
            // 8–12 перевіряє бек — текст 422 ляже під поле
            filterOperator: '=',
            inputProps: { maxLength: OTHER_CARRIER_EDRPOU_MAX_LENGTH },
        },
        contactInformation: textField(L.contactInformation),
        description: textField(L.description, true),
        comment: textField(L.comment),
        // Службова інформація — у формі лише показ
        createdAt: readOnly({
            label: L.createdAt,
            type: 'datetime',
            view: renderDateTimeView,
        }),
        createdBy: readOnly({
            label: L.createdBy,
            type: 'select',
            resource: 'Setting.User',
            view: renderUsersView,
        }),
        updatedAt: readOnly({
            label: L.updatedAt,
            type: 'datetime',
            view: renderDateTimeView,
        }),
        updatedBy: readOnly({
            label: L.updatedBy,
            type: 'select',
            resource: 'Setting.User',
            view: renderUsersView,
        }),
    },
    // 8 колонок у порядку легасі (`SPEC-010` §9.7)
    table: {
        showColumns: ['id', 'title', 'carTypes', 'countries', 'edrpou', 'contactInformation', 'description', 'comment'],
        dataSourceNoTransform: ['carTypes', 'countries', 'contactInformation', 'description', 'comment'],
        eventHandlers: {
            onBuildColumns: { AddOtherCarrierColumnRenderers },
        },
    },
    // Ті самі 8 полів фільтрами; «Контактна інформація», «Опис», «Коментар»
    // у легасі бек ігнорував (НП-11) — тепер вони працюють (П10-4 (а))
    filter: {
        includeFields: ['id', 'title', 'carTypes', 'countries', 'edrpou', 'contactInformation', 'description', 'comment'],
    },
    list: {
        pagination: { pageSize: 20 },
        sorter: { key: 'updatedAt', order: 'descend' },
    },
    form: {
        eventHandlers: {
            onBeforeSendSaveRequest: {
                OtherCarrierPayload: prepareOtherCarrierPayload,
            },
        },
    },
    detailConfig: {
        modeConfig: {
            view: { template: OtherCarrierFormTemplate },
            edit: { template: OtherCarrierFormTemplate },
            create: { template: OtherCarrierFormTemplate },
        },
    },
    drawer: {
        // Як картки CRM із коментарями праворуч (у легасі — шухляда 750)
        width: 1200,
    },
    restUrl: OTHER_CARRIER_REST_URL,
    pageUrl: '/other-carriers',
    pageConfig: {
        title: 'Інші перевізники',
        icon: <AiOutlineDatabase />,
        // Прямою адресою сторінку відкриває лише той, кому бек дає список:
        // решта бачить «Відсутній доступ» (`SPEC-014` Г6, С10.2)
        accessFn: () => CheckGlobalResourceAccessTask(OTHER_CARRIER_RESOURCE, 'read_collection'),
    },
    settings: {
        // Рядок пошуку `q` — підрядок назви або ЄДРПОУ (бек, `SEARCH_FIELDS`)
        searchFields: ['title', 'edrpou'],
    },
}
